/**
 * Re-queues failed OncoEMR task reply outbox rows via the retry endpoint.
 * @module useOncoemrTaskReplyRetry
 */

import { useCallback } from "react";

import { retryOncoemrTaskReply } from "../../../../../api/postCall/retryOncoemrTaskReply";

/** Inputs for {@link useOncoemrTaskReplyRetry}. */
export interface UseOncoemrTaskReplyRetryParams {
  orgId: string;
  /** Called after the API accepts the re-queued reply. */
  onReplyRetried?: () => void | Promise<void>;
}

/**
 * Wraps `POST .../replies/{idempotency_key}/retry` for failed outbox rows.
 *
 * @param params - Org scope and post-success callback.
 * @returns `retryFailedReply` to invoke from the conversation panel.
 */
export const useOncoemrTaskReplyRetry = ({
  orgId,
  onReplyRetried,
}: UseOncoemrTaskReplyRetryParams) => {
  const retryFailedReply = useCallback(
    async (idempotencyKey: string) => {
      if (!orgId || !idempotencyKey) {
        throw new Error("Missing task or organization context.");
      }

      const result = await retryOncoemrTaskReply(idempotencyKey, {
        org_id: orgId,
      });
      await onReplyRetried?.();
      return result;
    },
    [orgId, onReplyRetried],
  );

  return { retryFailedReply };
};
